import type { Artifact } from '@magimp/shared';

export type ChartArtifact = Extract<Artifact, { kind: 'chart' }>;
export type ChartType = ChartArtifact['chartType'];

export interface SeriesLookup {
  name: string;
  values: Map<string, number>;
}

export interface ChartModel {
  labels: string[];
  series: SeriesLookup[];
  min: number;
  max: number;
}

export const CHART_WIDTH = 640;
export const CHART_HEIGHT = 260;
export const CHART_PLOT = {
  left: 54,
  right: 18,
  top: 18,
  bottom: 48,
} as const;

export function buildChartModel(artifact: ChartArtifact): ChartModel {
  const labels: string[] = [];
  const seen = new Set<string>();
  const series = artifact.series.map((s) => {
    const values = new Map<string, number>();
    for (const point of s.points) {
      values.set(point.x, point.y);
      if (!seen.has(point.x)) {
        seen.add(point.x);
        labels.push(point.x);
      }
    }
    return { name: s.name, values };
  });
  const [min, max] =
    artifact.chartType === 'stacked_bar'
      ? stackedDomain(labels, series)
      : flatDomain(series.flatMap((s) => [...s.values.values()]));
  return { labels, series, min, max };
}

export function isSupportedChartType(value: unknown): value is ChartType {
  return value === 'bar' || value === 'line' || value === 'stacked_bar';
}

export function plotWidth(): number {
  return CHART_WIDTH - CHART_PLOT.left - CHART_PLOT.right;
}

export function plotHeight(): number {
  return CHART_HEIGHT - CHART_PLOT.top - CHART_PLOT.bottom;
}

export function xFor(index: number, count: number): number {
  if (count <= 1) return CHART_PLOT.left + plotWidth() / 2;
  return CHART_PLOT.left + (plotWidth() * index) / (count - 1);
}

export function yFor(value: number, min: number, max: number): number {
  return CHART_PLOT.top + ((max - value) / (max - min)) * plotHeight();
}

export function visibleXLabels(labels: string[]): Array<{ label: string; index: number }> {
  const step = Math.max(1, Math.ceil(labels.length / 6));
  return labels.map((label, index) => ({ label, index })).filter(({ index }) => index % step === 0);
}

export function shortLabel(label: string): string {
  return label.length > 12 ? `${label.slice(0, 11)}…` : label;
}

function flatDomain(values: number[]): [number, number] {
  if (values.length === 0) return [0, 1];
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  return paddedDomain(min, max);
}

function stackedDomain(labels: string[], series: SeriesLookup[]): [number, number] {
  let min = 0;
  let max = 0;
  for (const label of labels) {
    let positive = 0;
    let negative = 0;
    for (const s of series) {
      const value = s.values.get(label) ?? 0;
      if (value >= 0) positive += value;
      else negative += value;
    }
    max = Math.max(max, positive);
    min = Math.min(min, negative);
  }
  return paddedDomain(min, max);
}

function paddedDomain(min: number, max: number): [number, number] {
  if (min === max) {
    const pad = Math.max(1, Math.abs(max) * 0.1);
    return [min - pad, max + pad];
  }
  const pad = (max - min) * 0.08;
  return [min - pad, max + pad];
}
